import { ExpenseDescription } from "@/types/expenseDescription";
import { SelectOption } from "@/types/select";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Breakpoint,
  DatePicker,
  Form,
  FormInstance,
  FormItemProps,
  Input,
  InputNumber,
  Select,
  Typography,
} from "antd";
import { green, red } from "@ant-design/colors";
import { Supplier } from "@/types/supplier";
import { PREFIX, SUFFIX } from "../constants";
import { ExpenseType } from "@/types/expenseType";
import { ExpenseUnit } from "@/types/expenseUnit";
import {
  useGetExpenseDescriptions,
  useGetExpenseSuppliers,
  useGetExpenseUnits,
} from "./useExpenses";
import { getIsLastRowFull } from "../utils/expenseDrafts";
import dayjs, { Dayjs } from "dayjs";
import { LabeledValue } from "antd/es/select";
import {
  formatterCurrency,
  formatterQuantity,
  onKeyDownInputNumber,
  parserCurrency,
  parserQuantity,
} from "@/utils/inputNumber";
import { UpdateExpenseDescriptionFormValues } from "../../ManageExpense/api/expenseDescriptions";

// ===== TYPES =====
interface InputProps {
  component: React.ReactNode;
  itemProps: FormItemProps;
}

interface SearchableItem {
  id: string;
  name: string;
  is_active?: boolean;
  expense_type?: { id: string; name: string };
}

interface SearchState {
  isNew: boolean;
  isInactive: boolean;
  newOption?: SelectOption;
}

interface SearchableSelectConfig {
  expenseTypeId?: string;
  placeholder: string;
  label: string;
  requiredMessage: string;
}

interface UseGetInputProps {
  form: FormInstance;
  breakpoint?: Partial<Record<Breakpoint, boolean>>;
  expenseType?: ExpenseType;
  lastExpenseDate?: Dayjs;
  setDescriptionUpdates?: (
    description?: UpdateExpenseDescriptionFormValues
  ) => void;
}

// ===== CONSTANTS =====
const INITIAL_SEARCH_STATE: SearchState = {
  isNew: false,
  isInactive: false,
  newOption: undefined,
};

const SEARCHABLE_SELECT_CONFIGS = {
  description: {
    placeholder: "Select a description",
    label: "Description",
    requiredMessage: "Please select a description",
  },
  supplier: {
    placeholder: "Select a supplier",
    label: "Supplier",
    requiredMessage: "Please select a supplier",
  },
} as const;

// ===== UTILITY FUNCTIONS =====
const createSelectOption = (item: SearchableItem): SelectOption => ({
  value: item.id,
  label: item.name,
});

const createNewOption = (search: string): SelectOption => ({
  value: `${PREFIX.NEW}${search}`,
  label: `${search}${SUFFIX.NEW}`,
});

const createInactiveOption = (item: SearchableItem): SelectOption => ({
  value: `${PREFIX.INACTIVE}${item.id}`,
  label: `${item.name}${SUFFIX.INACTIVE}`,
});

const createInactiveOptionWithoutSuffix = (
  item: SearchableItem
): SelectOption => ({
  value: `${PREFIX.INACTIVE}${item.id}`,
  label: item.name,
});

const createDisabledOption = (
  search: string,
  typeName: string
): SelectOption => ({
  value: "",
  label: `${search} (${typeName})`,
  disabled: true,
});

const isItemInOtherExpenseType = (
  item: SearchableItem,
  search: string,
  expenseTypeId: string
): boolean => {
  return (
    "expense_type" in item &&
    item.expense_type?.id !== expenseTypeId &&
    item.name.toLowerCase() === search.toLowerCase()
  );
};

const isInactiveItem = (item: SearchableItem, search: string): boolean => {
  return (
    item.name.toLowerCase() === search.toLowerCase() && item.is_active === false
  );
};

const isExistingOption = (options: SelectOption[], search: string): boolean => {
  return options.some(
    (option) => option.label.toLowerCase() === search.toLowerCase()
  );
};

// ===== CUSTOM HOOKS =====
const useSearchState = () => {
  const [state, setState] = useState<SearchState>(INITIAL_SEARCH_STATE);

  const resetState = () => {
    setState(INITIAL_SEARCH_STATE);
  };

  const setNewState = () => {
    setState((prev) => ({
      isNew: true,
      isInactive: false,
      newOption: {
        value: prev.newOption?.value || "",
        label: prev.newOption?.label.replace(SUFFIX.NEW, "") || "",
      },
    }));
  };

  const setInactiveState = () => {
    setState((prev) => ({
      isNew: false,
      isInactive: true,
      newOption: {
        value: prev.newOption?.value || "",
        label: prev.newOption?.label.replace(SUFFIX.INACTIVE, "") || "",
      },
    }));
  };

  const setNewOptionState = useCallback((search: string) => {
    setState((prev) => ({
      ...prev,
      newOption: createNewOption(search),
    }));
  }, []);

  const setInactiveOptionState = useCallback((item: SearchableItem) => {
    setState((prev) => ({
      ...prev,
      newOption: createInactiveOption(item),
    }));
  }, []);

  const setInactiveOptionStateWithStatus = useCallback(
    (item: SearchableItem) => {
      setState(() => ({
        isNew: false,
        isInactive: true,
        newOption: createInactiveOptionWithoutSuffix(item),
      }));
    },
    []
  );

  const setDisabledState = useCallback((search: string, typeName: string) => {
    setState((prev) => ({
      ...prev,
      newOption: createDisabledOption(search, typeName),
    }));
  }, []);

  const clearNewOption = useCallback(() => {
    setState((prev) => ({ ...prev, newOption: undefined }));
  }, []);

  return {
    state,
    resetState,
    setNewState,
    setNewOptionState,
    setInactiveState,
    setInactiveOptionState,
    setInactiveOptionStateWithStatus,
    setDisabledState,
    clearNewOption,
  };
};

const useSelectOptions = <T extends SearchableItem>(
  data: T[] | undefined,
  expenseTypeId?: string
) => {
  return useMemo(() => {
    if (!data) return [];

    return data.reduce((acc: SelectOption[], item) => {
      // Filter by expense type if specified
      if (
        expenseTypeId &&
        "expense_type" in item &&
        item.expense_type?.id !== expenseTypeId
      ) {
        return acc;
      }

      // Only include active items
      if (item.is_active === false) {
        return acc;
      }

      acc.push(createSelectOption(item));
      return acc;
    }, []);
  }, [data, expenseTypeId]);
};

const useSearchHandler = <T extends SearchableItem>(
  data: T[] | undefined,
  selectOptions: SelectOption[],
  expenseTypeId: string | undefined,
  stateHandlers: {
    setNewOptionState: (search: string) => void;
    setInactiveOptionState: (item: SearchableItem) => void;
    setDisabledState: (search: string, typeName: string) => void;
    clearNewOption: () => void;
  }
) => {
  return useCallback(
    (search: string) => {
      if (!search || !data) return;

      // Check if item exists in different expense type
      if (expenseTypeId) {
        const itemInOtherType = data.find((item) =>
          isItemInOtherExpenseType(item, search, expenseTypeId)
        );

        if (itemInOtherType) {
          stateHandlers.setDisabledState(
            search,
            itemInOtherType.expense_type?.name || ""
          );
          return;
        }
      }

      // Check if inactive item exists
      const inactiveItem = data.find((item) => isInactiveItem(item, search));
      if (inactiveItem) {
        stateHandlers.setInactiveOptionState(inactiveItem);
        return;
      }

      // Check if item already exists
      if (isExistingOption(selectOptions, search)) {
        stateHandlers.clearNewOption();
        return;
      }

      // Set new item option
      stateHandlers.setNewOptionState(search);
    },
    [data, selectOptions, expenseTypeId, stateHandlers]
  );
};

const useChangeHandler = (stateHandlers: {
  setNewState: () => void;
  setInactiveState: () => void;
  resetState: () => void;
}) => {
  return useCallback(
    (value: string) => {
      if (value.startsWith(PREFIX.NEW)) {
        stateHandlers.setNewState();
        return;
      }

      if (value.startsWith(PREFIX.INACTIVE)) {
        stateHandlers.setInactiveState();
        return;
      }

      stateHandlers.resetState();
    },
    [stateHandlers]
  );
};

const usePrefixComponent = (isNew: boolean, isInactive: boolean) => {
  return useMemo(() => {
    if (isNew) {
      return (
        <Typography.Text style={{ fontSize: 12, color: green[6] }} strong>
          NEW
        </Typography.Text>
      );
    }

    if (isInactive) {
      return (
        <Typography.Text style={{ fontSize: 12, color: red[6] }} strong>
          INACTIVE
        </Typography.Text>
      );
    }

    return undefined;
  }, [isNew, isInactive]);
};

const useSearchableSelect = <T extends SearchableItem>(
  data: T[] | undefined,
  config: SearchableSelectConfig
) => {
  const { expenseTypeId, placeholder, label, requiredMessage } = config;

  const stateHandlers = useSearchState();
  const selectOptions = useSelectOptions(data, expenseTypeId);
  const handleSearch = useSearchHandler(
    data,
    selectOptions,
    expenseTypeId,
    stateHandlers
  );
  const handleChange = useChangeHandler(stateHandlers);
  const prefixComponent = usePrefixComponent(
    stateHandlers.state.isNew,
    stateHandlers.state.isInactive
  );

  const getValueProps = useCallback(
    (value: string | LabeledValue) => {
      const valueString =
        typeof value === "string" ? value : value?.value.toString();

      // Initialize inactive state if needed
      if (
        valueString?.startsWith(PREFIX.INACTIVE) &&
        stateHandlers.state.newOption === undefined
      ) {
        const inactiveId = valueString.replace(PREFIX.INACTIVE, "");
        const item = data?.find((item) => item.id === inactiveId);
        if (item) {
          stateHandlers.setInactiveOptionStateWithStatus(item);
        }
      }
      return { value };
    },
    [data, stateHandlers]
  );

  return {
    state: stateHandlers.state,
    selectOptions,
    prefixComponent,
    handleSearch,
    handleChange,
    resetState: stateHandlers.resetState,
    getValueProps,
    config: { placeholder, label, requiredMessage },
  };
};

// ===== SPECIFIC INPUT HOOKS =====
const useGetDescriptionInput = (
  setDescriptions: (descriptions: ExpenseDescription[]) => void,
  expenseTypeId?: string
): InputProps => {
  const descriptions = useGetExpenseDescriptions({
    config: {
      params: {
        expense_type_id: expenseTypeId,
        include_inactive: true,
        limit: -1,
      },
    },
    enabled: !!expenseTypeId,
  });

  const searchableSelect = useSearchableSelect<ExpenseDescription>(
    descriptions.data || [],
    {
      ...SEARCHABLE_SELECT_CONFIGS.description,
      expenseTypeId,
    }
  );

  useEffect(() => {
    setDescriptions(descriptions.data || []);
  }, [descriptions.data, setDescriptions]);

  return {
    itemProps: {
      name: "description_id",
      label: searchableSelect.config.label,
      rules: [
        { required: true, message: searchableSelect.config.requiredMessage },
      ],
      onReset: () => {
        searchableSelect.resetState();
      },
      getValueProps: searchableSelect.getValueProps,
    },
    component: (
      <Select
        prefix={searchableSelect.prefixComponent}
        showSearch
        placeholder={searchableSelect.config.placeholder}
        loading={descriptions.isFetching}
        filterOption={(input: string, option: SelectOption | undefined) =>
          option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
        }
        options={[
          ...searchableSelect.selectOptions,
          ...(searchableSelect.state.newOption
            ? [searchableSelect.state.newOption]
            : []),
        ]}
        style={{ width: "100%" }}
        onSearch={searchableSelect.handleSearch}
        onChange={searchableSelect.handleChange}
      />
    ),
  };
};

const useGetSupplierInput = (): InputProps => {
  const suppliers = useGetExpenseSuppliers({
    config: {
      params: {
        include_inactive: true,
        limit: -1,
      },
    },
    enabled: true,
  });

  const searchableSelect = useSearchableSelect<Supplier>(
    suppliers.data || [],
    SEARCHABLE_SELECT_CONFIGS.supplier
  );

  return {
    itemProps: {
      name: "supplier_id",
      label: searchableSelect.config.label,
      rules: [
        { required: true, message: searchableSelect.config.requiredMessage },
      ],
      onReset: searchableSelect.resetState,
      getValueProps: searchableSelect.getValueProps,
    },
    component: (
      <Select
        prefix={searchableSelect.prefixComponent}
        showSearch
        placeholder={searchableSelect.config.placeholder}
        loading={suppliers.isFetching}
        filterOption={(input: string, option: SelectOption | undefined) =>
          option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
        }
        options={[
          ...searchableSelect.selectOptions,
          ...(searchableSelect.state.newOption
            ? [searchableSelect.state.newOption]
            : []),
        ]}
        style={{ width: "100%" }}
        onSearch={searchableSelect.handleSearch}
        onChange={searchableSelect.handleChange}
      />
    ),
  };
};

const useGetUnitInput = (unitId?: string): InputProps => {
  const units = useGetExpenseUnits({
    config: {
      params: {
        limit: -1,
      },
    },
    enabled: true,
  });

  const selectOptions = useSelectOptions<ExpenseUnit>(
    units.data || [],
    undefined
  );

  return {
    itemProps: {
      name: "unit_id",
      label: "Unit",
      rules: [{ required: true, message: "Please select a unit" }],
    },
    component: (
      <Select
        showSearch
        placeholder="Select a unit"
        loading={units.isFetching}
        filterOption={(input: string, option: SelectOption | undefined) =>
          (option?.label?.toLowerCase().indexOf(input.toLowerCase()) ?? -1) >= 0
        }
        options={selectOptions}
        style={{ width: "100%" }}
        disabled={!!unitId}
      />
    ),
  };
};

// ===== SPECIFIC INPUT FUNCTIONS =====
export const getDateInput = (defaultValue: Dayjs = dayjs()): InputProps => {
  return {
    itemProps: {
      name: "date",
      label: "Date",
      rules: [{ required: true, message: "Please select a date" }],
      initialValue: defaultValue,
    },
    component: <DatePicker style={{ width: "100%" }} />,
  };
};

export const getPriceInput = (): InputProps => {
  return {
    itemProps: {
      name: "price",
      label: "Price",
      rules: [{ required: true, message: "Please enter the price" }],
    },
    component: (
      <InputNumber
        inputMode="decimal"
        controls={false}
        decimalSeparator=","
        precision={2}
        min={0}
        prefix="Rp"
        style={{ width: "100%" }}
        parser={parserCurrency}
        formatter={formatterCurrency}
        onKeyDown={onKeyDownInputNumber}
      />
    ),
  };
};

export const getQuantityInput = (): InputProps => {
  return {
    itemProps: {
      name: "quantity",
      label: "Quantity",
      rules: [{ required: true, message: "Please enter the quantity" }],
    },
    component: (
      <InputNumber
        inputMode="decimal"
        controls={false}
        decimalSeparator=","
        precision={2}
        min={0}
        style={{ width: "100%" }}
        parser={parserQuantity}
        formatter={formatterQuantity}
        onKeyDown={onKeyDownInputNumber}
      />
    ),
  };
};

export const getStaffNameInput = (): InputProps => {
  return {
    itemProps: {
      name: "staff_name",
      label: "Staff Name",
      rules: [{ required: true, message: "Please enter the staff name" }],
    },
    component: <Input style={{ width: "100%" }} />,
  };
};

// ===== MAIN HOOK =====
export const useGetInputs = ({
  form,
  breakpoint,
  expenseType,
  lastExpenseDate,
  setDescriptionUpdates,
}: UseGetInputProps): InputProps[] => {
  const [unitId, setUnitId] = useState<string>();
  const [descriptions, setDescriptions] = useState<ExpenseDescription[]>();
  const watchDescriptionId = Form.useWatch("description_id", form);
  const watchUnitId = Form.useWatch("unit_id", form);

  const descriptionInput = useGetDescriptionInput(
    setDescriptions,
    expenseType?.id
  );
  const supplierInput = useGetSupplierInput();
  const unitInput = useGetUnitInput(unitId);
  const dateInput = getDateInput(lastExpenseDate);
  const priceInput = getPriceInput();
  const quantityInput = getQuantityInput();
  const staffNameInput = getStaffNameInput();

  const inputFieldConfig = useMemo(
    () => ({
      date: dateInput,
      description: descriptionInput,
      price: priceInput,
      quantity: quantityInput,
      unit: unitInput,
      staff_name: staffNameInput,
      supplier: supplierInput,
    }),
    [
      descriptionInput,
      supplierInput,
      unitInput,
      dateInput,
      priceInput,
      quantityInput,
      staffNameInput,
    ]
  );

  // Generate inputs based on required fields
  const inputs = useMemo(() => {
    if (!expenseType?.required_fields) {
      return [];
    }

    const filteredFields = expenseType.required_fields
      .map((field) => {
        const config = inputFieldConfig[field as keyof typeof inputFieldConfig];
        return config;
      })
      .filter(Boolean) as InputProps[];

    // Add empty field in last item -1 if last row is full (for record expense form responsiveness)
    const isLastRowFull =
      breakpoint && !breakpoint.xs
        ? getIsLastRowFull(breakpoint, filteredFields.length)
        : false;

    if (isLastRowFull) {
      filteredFields.splice(filteredFields.length - 1, 0, {
        itemProps: {
          name: "empty",
          style: { display: "none" },
        },
        component: null,
      });
    }

    return filteredFields;
  }, [expenseType?.required_fields, inputFieldConfig, breakpoint]);

  // Auto select Unit on description change manually
  useEffect(() => {
    if (form && descriptions && watchDescriptionId) {
      const descriptionId =
        typeof watchDescriptionId === "string"
          ? watchDescriptionId
          : watchDescriptionId?.value;

      // clear prefix NEW and INACTIVE
      const descriptionIdWithoutPrefix = descriptionId?.replace(
        new RegExp(`^(${PREFIX.NEW}|${PREFIX.INACTIVE})`),
        ""
      );

      const foundUnitId = descriptions.find(
        (item) => item.id === descriptionIdWithoutPrefix
      )?.unit_id;
      setUnitId(foundUnitId);
      form.setFieldValue("unit_id", foundUnitId);
      return;
    }

    setUnitId(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptions, watchDescriptionId, form]);

  // If the selected description does not have a unit_id, but the user selects a unit, update the description's unit_id accordingly
  useEffect(() => {
    if (watchDescriptionId && watchUnitId && setDescriptionUpdates) {
      const descriptionId =
        typeof watchDescriptionId === "string"
          ? watchDescriptionId
          : watchDescriptionId?.value;

      const description = descriptions?.find(
        (item) => item.id === descriptionId
      );

      if (description && !description.unit_id) {
        setDescriptionUpdates({ ...description, unit_id: watchUnitId });
        return;
      }
    }

    setDescriptionUpdates?.(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchUnitId, watchDescriptionId, unitId]);

  return inputs;
};
